import React, { useState } from "react";
import CustomModal from "@/shared/components/ui/Modal/CustomModal";
import { FastForward, GitBranch, Loader2, X } from "lucide-react";
import { CampaignConditions, CampaignSteps } from "@/shared/utils/utils";
import StepAndConditionCard from "./StepConditionCard";
import CommonTitleDescription from "./CommonTitleDescription";
import SecondaryButton from "@/shared/components/ui/Button/SecondaryButton";
import { PrimaryButton } from "@/shared/components/ui/Button/PrimaryButton";

const StepConditionModal = ({
  isOpen,
  onClose,
  onSave,
  step,
  condition,
  branchMode = null,
  isCreatingStep,
  isUpdatingStep,
}) => {
  const isBranchStepOnly = branchMode === "no";
  const [selectedConditionId, setSelectedConditionId] = useState(
    CampaignConditions.find((c) => c?.text === condition?.conditions)?.id ||
      null,
  );
  const [selectedStepId, setSelectedStepId] = useState(
    CampaignSteps.find((s) => s?.text === step?.action)?.id || null,
  );

  // to do add edit functionality
  const handleConditionClick = (conditionId) => {
    setSelectedConditionId((prev) =>
      prev === conditionId ? null : conditionId,
    );
  };

  const handleStepClick = (stepId) => {
    setSelectedStepId((prev) => (prev === stepId ? null : stepId));
  };

  // RULES FOR SUBMISSION
  const isConditionSelected = selectedConditionId !== null;
  const isStepSelected = selectedStepId !== null;
  const isCreatingNew = step === null && condition === null;
  const canSubmit = isBranchStepOnly
    ? isStepSelected
    : isCreatingNew
    ? isStepSelected
    : isStepSelected || isConditionSelected;

  const handleSubmit = () => {
    if (!canSubmit || !onSave) return;

    const selectedStep = CampaignSteps.find((s) => s?.id === selectedStepId);

    const selectedCondition = selectedConditionId
      ? CampaignConditions.find((c) => c?.id === selectedConditionId)
      : null;

    const payload = {
      type: isBranchStepOnly
        ? "STEP"
        : selectedCondition && !selectedStep
          ? "CONDITION"
          : "STEP",
      step: {
        action: selectedStep?.action,
        channel: selectedStep?.channel,
      },
      condition: isBranchStepOnly ? null : {
        text: selectedCondition?.text,
      },
    };

    onSave(payload);
    handleClose();
  };

  const handleClose = () => {
    setSelectedConditionId(null);
    setSelectedStepId(null);
    onClose();
  };

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={handleClose}
      className="p-5 rounded-3xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 shadow-none"
      width="833px"
    >
      <>
        {/* CONDITIONS */}
        <div className="flex flex-col gap-3.5 pb-5">
          <div className="flex items-start justify-between w-full">
            <CommonTitleDescription
              Icon={
                <GitBranch
                  size={16}
                  className="text-secondary"
                  strokeWidth={1.7}
                />
              }
              title={
                isBranchStepOnly
                  ? "Add No Branch Step"
                  : condition === null
                    ? "Add Condition"
                    : "Edit Condition"
              }
              description={
                isBranchStepOnly
                  ? "Choose the action to run when the condition is not met."
                  : "Use conditions to build intelligent decision branches."
              }
            />

            <button
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full"
              onClick={handleClose}
            >
              <X size={20} className="text-gray-800 dark:text-gray-200" />
            </button>
          </div>

          {!isBranchStepOnly && (
            <div className="flex gap-3 flex-wrap">
              {CampaignConditions.map((condition) => (
                <StepAndConditionCard
                  key={condition?.id}
                  text={condition?.text}
                  Icon={condition?.Icon}
                  type="condition"
                  isSelected={selectedConditionId === condition?.id}
                  hasAnySelection={isConditionSelected}
                  onClick={() => handleConditionClick(condition.id)}
                />
              ))}
            </div>
          )}
        </div>

        {/* AND / OR */}
        {step === null && condition === null && !isBranchStepOnly && (
          <div className="flex items-center gap-2  w-full whitespace-nowrap">
            <div className="border-b border-gray-200 dark:border-gray-800 w-full" />
            <p className="text-gray-400 dark:text-gray-500 text-xs font-light">AND / OR</p>
            <div className="border-b border-gray-200 dark:border-gray-800 w-full" />
          </div>
        )}

        {/* STEPS */}
        {step === null && condition === null && (
          <div className="flex flex-col gap-3.5 py-5">
            <CommonTitleDescription
              Icon={
                <FastForward
                  size={16}
                  className="text-success-500"
                  strokeWidth={1.7}
                />
              }
              title={step !== null ? "Edit Step" : "Add Step"}
              description="Define the next action in this workflow."
            />

            <div className="flex gap-3 flex-wrap">
              {CampaignSteps.map((step) => (
                <StepAndConditionCard
                  key={step.id}
                  text={step.text}
                  Icon={step.Icon}
                  type="step"
                  isSelected={selectedStepId === step.id}
                  hasAnySelection={isStepSelected}
                  onClick={() => handleStepClick(step.id)}
                  secondaryText={step.secondaryText}
                />
              ))}
            </div>
          </div>
        )}

        {isCreatingNew &&
          selectedConditionId !== null &&
          selectedStepId === null && (
            <p className="text-error-500 text-xs font-light mb-2">
              After adding a condition, you must add a step.
            </p>
          )}
        {/* Footer */}
        <div className="flex gap-2 justify-between w-full">
          <SecondaryButton
            title="Cancel"
            onClick={handleClose}
            className="shadow-none"
          />
          <PrimaryButton
            onClick={handleSubmit}
            disabled={!canSubmit}
            startContent={
              isCreatingStep || isUpdatingStep ? (
                <Loader2 size={16} className="text-white" />
              ) : (
                false
              )
            }
          >
            {condition !== null || step !== null ? "Update" : "Submit"}
          </PrimaryButton>
        </div>
      </>
    </CustomModal>
  );
};

export default StepConditionModal;
